import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import { Picture } from "@/components/wpl/image/src/index";
import { Link } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";
import { Row } from "@/libraries/wpl/foundations/src";

import {
    Card,
    CardProps
} from "../../../../libraries/design/components/card/src/card";

const resourcesContainerCss = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center"
});

const headerCss = css({
    fontSize: "32px",
    lineHeight: "38px",
    fontWeight: 800,
    margin: "0 0 14px 0",
    textAlign: "center",
    [media.above.sm]: {
        fontSize: "48px",
        lineHeight: "56px",
        margin: "0 0 16px 0"
    }
});

const subtitleCss = css({
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 300,
    margin: "0 auto 20px",
    textAlign: "center",
    [media.only.sm]: {
        fontSize: "20px",
        lineHeight: "28px",
        maxWidth: "768px",
        margin: "0 auto 35px"
    },
    [media.above.md]: {
        fontSize: "28px",
        lineHeight: "40px",
        maxWidth: "100%"
    }
});

const ctaBlockCss = css({
    display: "flex",
    justifyContent: "center",
    width: "100%",
    margin: "50px 0 0",
    backgroundSize: "140% auto",
    backgroundRepeat: "no-repeat, no-repeat",
    position: "relative",
    padding: "25px 15px",
    backgroundImage:
        'url("https://dam-cdn.atl.orangelogic.com/AssetLink/33kawyc25lvmlphb6iweylcq75680n77.webp")',
    [media.only.sm]: {
        backgroundPosition: "left top , right -75px bottom",
        backgroundSize: "699px auto",
        height: "218px",
        padding: "0"
    },
    [media.above.md]: {
        backgroundImage:
            'url("https://dam-cdn.atl.orangelogic.com/AssetLink/q1pj4iteta1pu754qj7c3qq228c65h5q.webp")',
        backgroundPosition: "center",
        backgroundSize: "729px auto"
    }
});

const ctaBlockContentCss = css({
    alignItems: "center",
    color: wacToken("color.neutral.light0"),
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    textAlign: "center",
    [media.above.sm]: {
        padding: "16px 16px 48px"
    }
});

const ctaBlockTitleCss = css({
    fontSize: "24px",
    lineHeight: "30px",
    margin: "0"
});

const ctaBlockDetailCss = css({
    fontSize: "16px",
    lineHeight: "24px"
});

const imageCss = css({
    maxWidth: "48px",
    maxHeight: "48px",
    marginBlockEnd: "10px"
});

const cardBlockCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".row-layout": {
        padding: "0",
        width: "100%"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["img"]: {
        aspectRatio: "auto !important"
    }
});

export const ResourcesSection = () => {
    const intl = useIntl();

    const teamCardProps: CardProps = {
        title: intl.formatMessage({
            id: "NonprofitPage.resources.team.title",
            defaultMessage: "Team up with us"
        }),
        titleAs: "h3",
        body: intl.formatMessage({
            id: "NonprofitPage.resources.team.body",
            defaultMessage:
                "Get free, customized app and teamwork coaching sessions with Atlassian experts. No question is too big or too small."
        }),
        image: {
            image: {
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/7g2k6ru6bw83f33121usy12j03td6p88.webp",
                alt: "Partners for Nonprofits"
            }
        },
        callToAction: {
            href: useLocalizedHref("/teams/nonprofits/support-programs"),
            text: intl.formatMessage({
                id: "NonprofitPage.resources.team.link",
                defaultMessage: "Learn more"
            })
        }
    };

    const successCardProps: CardProps = {
        title: intl.formatMessage({
            id: "NonprofitPage.resources.success.title",
            defaultMessage: "Nonprofit teams success stories"
        }),
        titleAs: "h3",
        body: intl.formatMessage({
            id: "NonprofitPage.resources.success.body",
            defaultMessage:
                "Get inspired by teams like yours driving impact with Atlassian apps."
        }),
        image: {
            image: {
                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/6kp84so85h055h6o517q02jx1qp42p78.webp",
                alt: "Partners for Nonprofits"
            }
        },
        callToAction: {
            href: useLocalizedHref("/teams/nonprofits/resources"),
            text: intl.formatMessage({
                id: "NonprofitPage.resources.success.link",
                defaultMessage: "See all use cases"
            })
        }
    };
    return (
        <div css={resourcesContainerCss}>
            <h2 css={headerCss}>
                <FormattedMessage
                    id="NonprofitPage.resources.header.v1"
                    defaultMessage="Explore resources"
                />
            </h2>
            <h3 css={subtitleCss}>
                <FormattedMessage
                    id="NonprofitPage.resources.subtitle"
                    defaultMessage="While you wait for your Community License to be approved…"
                />
            </h3>
            <div css={cardBlockCss}>
                <Row css={cardBlockCss} layout="6-6">
                    <Card {...teamCardProps} />
                    <Card {...successCardProps} />
                </Row>
            </div>
            <div css={ctaBlockCss}>
                <div css={ctaBlockContentCss}>
                    <div css={imageCss}>
                        <Picture
                            image={{
                                src: "https://dam-cdn.atl.orangelogic.com/AssetLink/60gejft8yfd0xxjy5rnc2u822245u1t0.svg",
                                alt: ""
                            }}
                        />
                    </div>
                    <h3 css={ctaBlockTitleCss}>
                        <FormattedMessage
                            id="NonprofitPage.resources.cta.title"
                            defaultMessage="Interested in self-managed apps?"
                        />
                    </h3>
                    <p css={ctaBlockDetailCss}>
                        <FormattedMessage
                            id="NonprofitPage.resources.cta.content"
                            defaultMessage="Data Center versions for select apps are available for free."
                        />
                    </p>
                    <Link
                        text={intl.formatMessage({
                            id: "NonprofitPage.eligible.cta.content.link",
                            defaultMessage: "Learn more"
                        })}
                        href="/licensing/purchase-licensing#pricing-discounts"
                        type="primary"
                        bgColor="bgBlackBlue"
                        dataEventProps={{
                            "data-event-component": "linkButton",
                            "data-event-container": "Resources section",
                            "data-event": "clicked",
                            "data-product-key": "nonProfitCalculator"
                        }}
                    />
                </div>
            </div>
        </div>
    );
};
ResourcesSection.displayName = "ResourcesSection";
